import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMeCore } from "../../../cores/me";
import { EmailAiWriter } from "./EmailAiWriter";
import { DEFAULT_EMAIL_HTML, DEFAULT_RICH_HTML, isAdvancedEmailHtml } from "./emailHtmlUtils";
import { EmailHtmlPreview } from "./EmailHtmlPreview";
import { EmailRichEditor } from "./EmailRichEditor";
import { EmailMktModal } from "./shared";
import {
  EmailVisualBuilder,
  EmailVisualBuilderHandle,
} from "./EmailVisualBuilder";

export { DEFAULT_EMAIL_HTML, DEFAULT_RICH_HTML };

interface EmailComposerProps {
  open: boolean;
  subject: string;
  html: string;
  initialMode?: "rich" | "visual" | "html" | "preview";
  fromEmail?: string;
  fromName?: string;
  audienceName?: string;
  audienceCount?: number;
  marketingLists?: { id: string; name: string; subscribers: number }[];
  targetListId?: string | null;
  onTargetListIdChange?: (listId: string) => void;
  saving?: boolean;
  onSubjectChange: (value: string) => void;
  onHtmlChange: (value: string) => void;
  onClose: () => void;
  onSaveDraft: () => void;
  onSendTest?: () => void;
  onSendNow?: () => void;
  onPublish?: () => void;
  publishLabelId?: string;
  saveLabelId?: string;
}

type EditorMode = "rich" | "visual" | "html" | "preview";

const mapInitialMode = (
  mode: EmailComposerProps["initialMode"] = "rich"
): EditorMode => {
  if (mode === "preview") return "preview";
  if (mode === "html") return "html";
  if (mode === "visual") return "visual";
  return "rich";
};

export const EmailComposer = ({
  open,
  subject,
  html,
  initialMode = "rich",
  fromEmail,
  fromName,
  audienceName,
  audienceCount,
  marketingLists = [],
  targetListId,
  onTargetListIdChange,
  saving = false,
  onSubjectChange,
  onHtmlChange,
  onClose,
  onSaveDraft,
  onSendTest,
  onSendNow,
  onPublish,
  publishLabelId = "app.platformAutomations.template.publish",
  saveLabelId = "app.emailMarketing.editor.saveDraft",
}: EmailComposerProps) => {
  const intl = useIntl();
  const { company } = useMeCore();
  const builderRef = useRef<EmailVisualBuilderHandle>(null);
  const [modeOverride, setModeOverride] = useState<EditorMode | null>(null);
  const [pendingMode, setPendingMode] = useState<EditorMode | null>(null);

  const editorMode = modeOverride ?? mapInitialMode(initialMode);
  const previewHtml = useMemo(() => html || DEFAULT_RICH_HTML, [html]);

  const prevEditorModeRef = useRef<EditorMode>(editorMode);

  useEffect(() => {
    if (open) {
      setModeOverride(null);
    }
  }, [open, initialMode]);

  useEffect(() => {
    if (
      prevEditorModeRef.current === "html" &&
      editorMode === "visual" &&
      builderRef.current
    ) {
      builderRef.current.setContent(html || DEFAULT_EMAIL_HTML);
    }
    prevEditorModeRef.current = editorMode;
  }, [editorMode, html]);

  const handleAiApply = (nextSubject: string, nextHtml: string) => {
    onSubjectChange(nextSubject);
    onHtmlChange(nextHtml);
    if (editorMode === "visual") {
      builderRef.current?.setContent(nextHtml);
    } else {
      setModeOverride("rich");
    }
  };

  if (!open) {
    return null;
  }

  const wrapSelection = (openTag: string, closeTag: string) => {
    const textarea = document.getElementById(
      "email-mkt-editor-body"
    ) as HTMLTextAreaElement | null;
    if (!textarea) {
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = html.slice(start, end) || "texto";
    const next = `${html.slice(0, start)}${openTag}${selected}${closeTag}${html.slice(
      end
    )}`;
    onHtmlChange(next);
  };

  const handleModeChange = (next: EditorMode) => {
    if (
      (editorMode === "html" || editorMode === "preview") &&
      (next === "rich" || next === "visual") &&
      isAdvancedEmailHtml(html)
    ) {
      setPendingMode(next);
      return;
    }
    setModeOverride(next);
  };

  const confirmModeChange = () => {
    if (pendingMode) {
      setModeOverride(pendingMode);
      setPendingMode(null);
    }
  };

  return (
    <div className="email-mkt__composer-backdrop" onClick={onClose}>
      <div
        className="email-mkt__composer email-mkt__composer--builder"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="email-mkt__composer-header email-mkt__composer-header--builder">
          <div className="email-mkt__composer-header-main">
            <span className="email-mkt__eyebrow">
              <FormattedMessage id="app.emailMarketing.editor.title" />
            </span>
            <div className="email-mkt__composer-fields">
              <label className="email-mkt__composer-subject-row">
                <span>
                  <FormattedMessage id="app.emailMarketing.editor.subject" />
                </span>
                <input
                  value={subject}
                  onChange={(event) => onSubjectChange(event.target.value)}
                  placeholder={intl.formatMessage({
                    id: "app.emailMarketing.editor.subjectPlaceholder",
                  })}
                />
              </label>
              {marketingLists.length > 0 && onTargetListIdChange ? (
                <label className="email-mkt__composer-subject-row email-mkt__composer-list-row">
                  <span>
                    <FormattedMessage id="app.emailMarketing.editor.selectList" /> *
                  </span>
                  <select
                    value={targetListId || marketingLists[0]?.id || ""}
                    onChange={(event) => onTargetListIdChange(event.target.value)}
                    aria-required="true"
                  >
                    {marketingLists.map((list) => (
                      <option key={list.id} value={list.id}>
                        {`${list.name} (${list.subscribers} ${intl.formatMessage({
                          id: "app.emailMarketing.editor.recipients",
                        })})`}
                      </option>
                    ))}
                  </select>
                  <small className="email-mkt__copy email-mkt__copy--muted">
                    <FormattedMessage id="app.emailMarketing.editor.selectListHelp" />
                  </small>
                </label>
              ) : audienceName ? (
                <p className="email-mkt__copy email-mkt__composer-audience">
                  <FormattedMessage id="app.emailMarketing.editor.audience" />:{" "}
                  <strong>{audienceName}</strong>
                  {typeof audienceCount === "number" && audienceCount > 0 && (
                    <span className="email-mkt__audience-count">
                      ({audienceCount}{" "}
                      <FormattedMessage id="app.emailMarketing.editor.recipients" />)
                    </span>
                  )}
                </p>
              ) : null}
            </div>
            <div className="email-mkt__composer-meta">
              {fromEmail && (
                <p className="email-mkt__copy email-mkt__composer-from">
                  <FormattedMessage id="app.emailMarketing.lists.fromAddress" />:{" "}
                  <strong>{fromEmail}</strong>
                  {fromName && fromName !== fromEmail ? ` (${fromName})` : ""}
                </p>
              )}
            </div>
          </div>

          <div className="email-mkt__composer-header-actions">
            <div className="email-mkt__composer-toolbar-tabs">
              <button
                type="button"
                className={editorMode === "rich" ? "is-active" : ""}
                onClick={() => handleModeChange("rich")}
              >
                <FormattedMessage id="app.emailMarketing.editor.write" />
              </button>
              <button
                type="button"
                className={editorMode === "visual" ? "is-active" : ""}
                onClick={() => handleModeChange("visual")}
              >
                <FormattedMessage id="app.emailMarketing.editor.visual" />
              </button>
              <button
                type="button"
                className={editorMode === "html" ? "is-active" : ""}
                onClick={() => handleModeChange("html")}
              >
                <FormattedMessage id="app.emailMarketing.editor.html" />
              </button>
              <button
                type="button"
                className={editorMode === "preview" ? "is-active" : ""}
                onClick={() => handleModeChange("preview")}
              >
                <FormattedMessage id="app.emailMarketing.editor.preview" />
              </button>
            </div>
            <button
              type="button"
              className="email-mkt__ghost-btn email-mkt__composer-close"
              onClick={onClose}
            >
              <FormattedMessage id="app.emailMarketing.emails.wizard.close" />
            </button>
          </div>
        </header>

        <div
          className={`email-mkt__composer-body email-mkt__composer-body--builder${
            editorMode === "preview" ? " email-mkt__composer-body--preview-mode" : ""
          }`}
        >
          {editorMode === "rich" ? (
            <div className="email-mkt__composer-form email-mkt__composer-form--full">
              <EmailRichEditor
                html={html}
                onChange={onHtmlChange}
                placeholder={intl.formatMessage({
                  id: "app.emailMarketing.editor.richPlaceholder",
                })}
              />
            </div>
          ) : editorMode === "visual" ? (
            <div className="email-mkt__builder-studio-wrap">
              <div className="email-mkt__builder-main email-mkt__builder-main--studio">
                <EmailVisualBuilder
                  ref={builderRef}
                  html={html}
                  onHtmlChange={onHtmlChange}
                />
              </div>
              <aside className="email-mkt__builder-ai-panel">
                <EmailAiWriter companyId={company?.id} onApply={handleAiApply} />
              </aside>
            </div>
          ) : editorMode === "html" ? (
            <div className="email-mkt__composer-form email-mkt__composer-form--full">
              {isAdvancedEmailHtml(html) && (
                <p className="email-mkt__copy email-mkt__copy--muted email-mkt__html-hint">
                  <FormattedMessage id="app.emailMarketing.editor.advancedHtmlHint" />
                </p>
              )}
              <div className="email-mkt__composer-toolbar">
                <button
                  type="button"
                  onClick={() => wrapSelection("<strong>", "</strong>")}
                >
                  B
                </button>
                <button
                  type="button"
                  onClick={() => wrapSelection("<h2>", "</h2>")}
                >
                  H
                </button>
                <button
                  type="button"
                  onClick={() => wrapSelection("<p>", "</p>")}
                >
                  P
                </button>
                <button
                  type="button"
                  onClick={() =>
                    wrapSelection(
                      '<a href="https://wembii.com" style="color:#2563eb">',
                      "</a>"
                    )
                  }
                >
                  Link
                </button>
              </div>
              <textarea
                id="email-mkt-editor-body"
                className="email-mkt__composer-textarea email-mkt__composer-textarea--full"
                value={html}
                onChange={(event) => onHtmlChange(event.target.value)}
                rows={18}
              />
            </div>
          ) : (
            <EmailHtmlPreview
              html={previewHtml}
              title={intl.formatMessage({
                id: "app.emailMarketing.editor.preview",
              })}
            />
          )}
        </div>

        <footer className="email-mkt__composer-footer">
          <span className="email-mkt__composer-autosave">
            <FormattedMessage id="app.emailMarketing.editor.autosave" />
          </span>
          <div className="email-mkt__composer-actions">
            <button
              type="button"
              className="email-mkt__ghost-btn"
              disabled={saving}
              onClick={onSaveDraft}
            >
              <FormattedMessage id={saveLabelId} />
            </button>
            {onSendTest && (
              <button
                type="button"
                className="email-mkt__ghost-btn"
                disabled={saving}
                onClick={onSendTest}
              >
                <FormattedMessage id="app.emailMarketing.emails.actions.testSend" />
              </button>
            )}
            {onPublish && (
              <button
                type="button"
                className="email-mkt__primary-btn"
                disabled={saving}
                onClick={onPublish}
              >
                <FormattedMessage id={publishLabelId} />
              </button>
            )}
            {onSendNow && !onPublish && (
              <button
                type="button"
                className="email-mkt__primary-btn"
                disabled={saving}
                onClick={onSendNow}
              >
                <FormattedMessage id="app.emailMarketing.emails.wizard.sendNow" />
              </button>
            )}
          </div>
        </footer>
      </div>

      <EmailMktModal
        elevated
        open={Boolean(pendingMode)}
        title={
          <FormattedMessage id="app.emailMarketing.editor.advancedHtmlConfirmTitle" />
        }
        onClose={() => setPendingMode(null)}
        footer={
          <>
            <button
              type="button"
              className="email-mkt__ghost-btn"
              onClick={() => setPendingMode(null)}
            >
              <FormattedMessage id="app.emailMarketing.editor.advancedHtmlConfirmCancel" />
            </button>
            <button
              type="button"
              className="email-mkt__primary-btn"
              onClick={confirmModeChange}
            >
              <FormattedMessage id="app.emailMarketing.editor.advancedHtmlConfirmContinue" />
            </button>
          </>
        }
      >
        <p className="email-mkt__copy">
          <FormattedMessage id="app.emailMarketing.editor.advancedHtmlWarning" />
        </p>
      </EmailMktModal>
    </div>
  );
};
